import { ENV } from '@constants/constants';

export enum Target {
  MiddleTier = 'MiddleTier',
  Sitecore = 'Sitecore',
  Public = 'Public',
  Next = 'Next'
}

interface GetUrlOptions {
  forceClient?: boolean;
  appendPathname?: string;
  queryParams?: { [key: string]: any };
  isMockUrl?: boolean;
  target?: Target;
}
type GetMockUrlOptions = Omit<GetUrlOptions, 'isMockUrl'>;

const ExternalUrlMap: Partial<{ [key in Target]: string }> = {
  [Target.MiddleTier]: '/mt-api',
  [Target.Sitecore]: '/sc-api',
  [Target.Public]: ENV.NEXT_PUBLIC_URL_WWW_EXTERNAL,
  [Target.Next]: '/next-api'
};

const ExternalUrlMockMap: Partial<{ [key in Target]: string }> = {
  [Target.MiddleTier]: '/mock-api/api',
  [Target.Sitecore]: '/mock-api/sc'
};

const InternalUrlMap: Partial<{ [key in Target]: string }> = {
  [Target.MiddleTier]: process.env.URL_API_INTERNAL || '',
  [Target.Sitecore]: process.env.URL_CD_INTERNAL || ''
};

const InternalUrlMockMap: Partial<{ [key in Target]: string }> = {
  [Target.MiddleTier]: `${process.env.URL_MOCK_INTERNAL}/api`,
  [Target.Sitecore]: `${process.env.URL_MOCK_INTERNAL}/sc`
};

/**
 * A service class for generating URLs based on various environment flags and options.
 * This class handles differentiating between Docker, local, server rendering, and client rendering environments.
 */
export class UrlsService {
  isDocker: boolean;
  isLocal: boolean;
  isServerRendering: boolean;
  isClientRendering: boolean;
  fakeDomain: string;

  constructor() {
    this.isDocker = !!process?.env?.DOTNET_RUNNING_IN_CONTAINER;
    this.isLocal = !process?.env?.DOTNET_RUNNING_IN_CONTAINER ?? true;

    this.isServerRendering = typeof window === 'undefined';
    this.isClientRendering = typeof window !== 'undefined';
    this.fakeDomain = 'https://remove-me';
  }

  /**
   * Gets the base URL for API calls. The base URL can vary based on whether the code is running on the client or server,
   * and whether a mock API should be used.
   *
   * @param useMock - Optional flag to determine if the mock API URL should be used.
   * @param forceClient - Optional flag to force the function to return a client-side URL regardless of the actual environment.
   * @returns The base URL as a string.
   */
  getBaseURL(useMock?: boolean, forceClient?: boolean, target?: Target): string {
    target = target ?? Target.MiddleTier; // eslint-disable-line no-param-reassign

    if (this.isClientRendering || forceClient) {
      if (target === Target.Public) {
        return `${ExternalUrlMap[target]}`;
      }

      const domain = this.isClientRendering ? document.location.origin : this.fakeDomain;
      return `${domain}${useMock ? ExternalUrlMockMap[target] : ExternalUrlMap[target]}`;
    }
    //@ts-expect-error: -
    return useMock ? InternalUrlMockMap[target] : InternalUrlMap[target];
  }

  /**
   * Constructs a URL based on a base path, adding a leading slash if necessary,
   * and optionally appending query parameters.
   *
   * @param url - The path to append to the base URL. It ensures that the path starts with a slash.
   * @param options - An options object with the following properties:
   *   @param forceClient - Boolean that indicates whether to force the URL to include client-specific paths or parameters.
   *   @param queryParams - An optional object containing query parameters to append to the URL.
   *                        The values of these parameters are converted to lowercase strings.
   *                        If not provided, no query parameters are added.
   *   @param isMockUrl - When true the URL returned is for the mocks server
   * @returns Returns the fully constructed URL as a string.
   */
  getUrl(
    url: string,
    {
      appendPathname = undefined,
      forceClient = false,
      isMockUrl = false,
      queryParams = undefined,
      target = Target.MiddleTier
    }: GetUrlOptions = {}
  ): string {
    if (!url.startsWith('/')) {
      // eslint-disable-next-line no-param-reassign
      url = `/${url}`;
    }
    const uri = new URL(`${this.getBaseURL(isMockUrl, forceClient, target)}${url}`);

    if (appendPathname) {
      // Handle pathname = '/path/', appendPathname = '/appended/' and combinations thereof
      const nextPathname = [...uri.pathname.split('/'), ...appendPathname.split('/')];
      uri.pathname = nextPathname.filter((part) => part !== '').join('/');
    }

    if (queryParams) {
      const params = new URLSearchParams(uri.search);
      Object.entries(queryParams).forEach(([key, value]) => params.set(key, String(value)));
      uri.search = params.toString();
    }

    return uri.toString().replace(this.fakeDomain, '');
  }

  /**
   * Helper method to construct a mock URL, simplifying the call to `getUrl` with `isMockUrl` preset to `true`.
   *
   * @param url - The endpoint path for the mock URL.
   * @param options - Additional options, excluding `isMockUrl` as it's set to `true` by default.
   * @returns The constructed mock URL.
   */
  getMockUrl(
    url: string,
    { forceClient = false, appendPathname = '', queryParams = undefined, target = Target.MiddleTier }: GetMockUrlOptions = {}
  ): string {
    return this.getUrl(url, { forceClient, appendPathname, queryParams, isMockUrl: true, target: target });
  }
}

const urlsService = new UrlsService();
export default urlsService;
